import { useMemo, useState } from 'react';
import type { DayRow } from '../models';
import * as fmt from '../format';

/// One interactive chart, used for every graph on the Progress page.
///
/// Hand-drawn SVG rather than a charting library: everything plotted here is a
/// figure the API already computed, so the work is axes and hit-testing rather
/// than analysis — not enough to justify a dependency several times the size
/// of the whole app.
///
/// Three pieces of interaction, and no more. Series can be switched off, so a
/// busy chart can be reduced to the comparison you actually want. Hovering
/// reads every visible series at that day. And the page above chooses how far
/// back to look, because a 300-day walk squeezed into 600 pixels is a texture
/// rather than a chart.

export interface Series {
  key: string;
  label: string;
  value: (row: DayRow) => number;
  kind?: 'line' | 'area' | 'bar';
  /** Which of the four plot colours to use. */
  tone?: 1 | 2 | 3 | 4;
  format?: (value: number) => string;
  /** Off until switched on, for the secondary comparisons. */
  hiddenByDefault?: boolean;
}

const WIDTH = 680;
const PAD_X = 44;
const PAD_TOP = 16;
const PAD_BOTTOM = 26;

export function TimeSeries({
  rows,
  series,
  height = 220,
  zeroLine = false,
  invert = false,
}: {
  rows: DayRow[];
  series: Series[];
  height?: number;
  /** Draw a rule at zero — meaningful when a series goes negative. */
  zeroLine?: boolean;
  /** For rank, where 1 is the best and belongs at the top. */
  invert?: boolean;
}) {
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(series.filter((s) => s.hiddenByDefault).map((s) => s.key)),
  );
  const [hover, setHover] = useState<number | null>(null);

  const visible = series.filter((s) => !hidden.has(s.key));

  const scale = useMemo(() => {
    const values = visible.flatMap((s) => rows.map(s.value)).filter(Number.isFinite);
    if (values.length === 0) return null;
    let min = Math.min(...values);
    let max = Math.max(...values);
    // Bars and areas are read against zero; lines are not, so they keep a
    // tight range and stay legible when the whole series sits high.
    if (visible.some((s) => s.kind === 'bar' || s.kind === 'area')) {
      min = Math.min(min, 0);
      max = Math.max(max, 0);
    }
    if (zeroLine) {
      min = Math.min(min, 0);
      max = Math.max(max, 0);
    }
    if (min === max) { min -= 1; max += 1; }
    // Breathing room, but never across zero: a cumulative distance axis that
    // starts at minus thirty-eight miles is nonsense, and the padding was
    // happily producing one.
    const pad = (max - min) * 0.08;
    const padded = { min: min - pad, max: max + pad };
    if (min >= 0) padded.min = Math.max(padded.min, 0);
    if (max <= 0) padded.max = Math.min(padded.max, 0);
    return padded;
  }, [rows, visible, zeroLine]);

  if (rows.length < 2) return <p className="muted">Not enough days yet.</p>;
  if (!scale) return <p className="muted">Nothing selected.</p>;

  const plotW = WIDTH - PAD_X * 2;
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const x = (i: number) => PAD_X + (rows.length === 1 ? plotW / 2 : (i / (rows.length - 1)) * plotW);
  const y = (v: number) => {
    const t = (v - scale.min) / (scale.max - scale.min);
    return invert ? PAD_TOP + t * plotH : PAD_TOP + (1 - t) * plotH;
  };

  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      // Never hide the last one; an empty chart looks broken rather than
      // filtered.
      if (next.has(key)) next.delete(key);
      else if (visible.length > 1) next.add(key);
      return next;
    });

  /// Nearest day to the pointer. The SVG is scaled by CSS, so client pixels
  /// have to be converted back into viewBox units before they mean anything.
  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const viewX = ((event.clientX - box.left) / box.width) * WIDTH;
    const ratio = (viewX - PAD_X) / plotW;
    const index = Math.round(ratio * (rows.length - 1));
    setHover(index >= 0 && index < rows.length ? index : null);
  };

  const barWidth = Math.max(plotW / rows.length - 1, 1);
  // Resolved once, so nothing below has to re-check that the index is in range.
  const hovered = hover === null ? null : (rows[hover] ?? null);
  const first = rows[0];
  const last = rows[rows.length - 1];

  return (
    <div className="ts">
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="ts__svg"
        role="img"
        aria-label={series.map((s) => s.label).join(', ')}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <text x={4} y={y(scale.max) + 4} className="ts__axis">{axisLabel(scale.max, series)}</text>
        <text x={4} y={y(scale.min) + 4} className="ts__axis">{axisLabel(scale.min, series)}</text>

        {zeroLine && scale.min < 0 && scale.max > 0 && (
          <line x1={PAD_X} x2={WIDTH - PAD_X} y1={y(0)} y2={y(0)} className="ts__zero" />
        )}

        {visible.map((s) => {
          const points = rows.map((row, i) => [x(i), y(s.value(row))] as const);
          if (s.kind === 'bar') {
            return (
              <g key={s.key} className={`ts__s ts__s--${s.tone ?? 1}`}>
                {rows.map((row, i) => {
                  const value = s.value(row);
                  const top = Math.min(y(value), y(0));
                  return (
                    <rect
                      key={row.date}
                      x={x(i) - barWidth / 2}
                      y={top}
                      width={barWidth}
                      height={Math.abs(y(value) - y(0))}
                      className="ts__bar"
                    />
                  );
                })}
              </g>
            );
          }
          const line = points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px},${py}`).join(' ');
          const edgeStart = points[0];
          const edgeEnd = points[points.length - 1];
          return (
            <g key={s.key} className={`ts__s ts__s--${s.tone ?? 1}`}>
              {s.kind === 'area' && edgeStart && edgeEnd && (
                <path
                  d={`${line} L${edgeEnd[0]},${y(0)} L${edgeStart[0]},${y(0)} Z`}
                  className="ts__area"
                />
              )}
              <path d={line} className="ts__line" />
            </g>
          );
        })}

        {hovered && hover !== null && (
          <g className="ts__cursor">
            <line x1={x(hover)} x2={x(hover)} y1={PAD_TOP} y2={PAD_TOP + plotH} />
            {visible.map((s) => (
              <circle
                key={s.key}
                cx={x(hover)}
                cy={y(s.value(hovered))}
                r={3.5}
                className={`ts__dot ts__s--${s.tone ?? 1}`}
              />
            ))}
          </g>
        )}

        {first && (
          <text x={PAD_X} y={height - 6} className="ts__axis">{fmt.shortDate(first.date)}</text>
        )}
        {last && (
          <text x={WIDTH - PAD_X} y={height - 6} textAnchor="end" className="ts__axis">
            {fmt.shortDate(last.date)}
          </text>
        )}
      </svg>

      {hovered && hover !== null && (
        <div className="ts__tip" style={{ left: `${((x(hover) - PAD_X) / plotW) * 100}%` }}>
          <p className="ts__tip-date">{fmt.mediumDate(hovered.date)}</p>
          {visible.map((s) => (
            <p key={s.key} className={`ts__tip-row ts__s--${s.tone ?? 1}`}>
              <span>{s.label}</span>
              <strong>{(s.format ?? fmt.miles)(s.value(hovered))}</strong>
            </p>
          ))}
        </div>
      )}

      <div className="ts__legend">
        {series.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`ts__key ts__s--${s.tone ?? 1}${hidden.has(s.key) ? ' ts__key--off' : ''}`}
            aria-pressed={!hidden.has(s.key)}
            onClick={() => toggle(s.key)}
          >
            <span className="ts__swatch" />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/// Axis numbers borrow the first series' formatter, since every series on one
/// chart is in the same unit.
function axisLabel(value: number, series: Series[]): string {
  const format = series[0]?.format ?? fmt.miles;
  return format(value);
}
